import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../../components/Layout";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { FilePdf, ArrowClockwise, Warning, MapPin, Database, CheckCircle, Lightning, CloudArrowDown, FileMagnifyingGlass } from "@phosphor-icons/react";
import { PerizieMonitor } from "./PerizieMonitor";
import { OcrPanel } from "./OcrPanel";
import { KpiComparison } from "./KpiComparison";
import { SourceAudit } from "./SourceAudit";

const CLASS_META = {
    supportata: { label: "Supportata", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
    limitata: { label: "Limitata", cls: "bg-amber-50 text-amber-700 border-amber-300" },
    non_supportata: { label: "Non supportata", cls: "bg-rose-50 text-rose-700 border-rose-300" },
};

const READINESS_META = {
    pronto: { cls: "bg-emerald-50 text-emerald-700 border-emerald-300", label: "Pronto" },
    beta_limitata: { cls: "bg-amber-50 text-amber-700 border-amber-300", label: "Beta limitata" },
    non_pronto: { cls: "bg-rose-50 text-rose-700 border-rose-300", label: "Non pronto" },
};

function ClassBadge({ value }) {
    const m = CLASS_META[value] || CLASS_META.non_supportata;
    return <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${m.cls}`}>{m.label}</span>;
}

function QualityBar({ label, pct, testid }) {
    const color = pct >= 70 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-500" : "bg-rose-500";
    return (
        <div data-testid={testid}>
            <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">{label}</span>
                <span className="text-xs font-bold tabular-nums">{pct}%</span>
            </div>
            <div className="h-2 bg-gray-100 overflow-hidden">
                <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

function DistroBar({ label, value, max, testid }) {
    const pct = max ? Math.round((value / max) * 100) : 0;
    return (
        <div className="flex items-center gap-3" data-testid={testid}>
            <div className="w-28 shrink-0 text-xs text-gray-700 truncate text-right">{label}</div>
            <div className="flex-1 h-5 bg-gray-100 overflow-hidden">
                <div className="h-full bg-black flex items-center justify-end px-2" style={{ width: `${Math.max(pct, 8)}%` }}>
                    <span className="text-[10px] font-bold text-white tabular-nums">{value}</span>
                </div>
            </div>
        </div>
    );
}

export default function CoverageAudit() {
    const { user } = useAuth();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [recomputing, setRecomputing] = useState(false);
    const [refetching, setRefetching] = useState(false);
    const [enrichState, setEnrichState] = useState(null);
    const [docsRunning, setDocsRunning] = useState(false);
    const [docsState, setDocsState] = useState(null);
    const pollRef = useRef(null);
    const docsPollRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/coverage");
            setReport(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchEnrichStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/enrich/status");
            setEnrichState(data.state);
            if (data.state && !data.state.running && pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
                setRefetching(false);
                toast.success(
                    `Re-fetch completato: ${data.state.enriched}/${data.state.total} arricchite` +
                    (data.state.aborted_throttled ? " (interrotto: sito sotto throttling)" : "")
                );
                load();
            }
        } catch (err) {
            // silent
        }
    }, [load]);

    const fetchDocsStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/enrich/documents/status");
            setDocsState(data.state);
            if (data.state && !data.state.running && docsPollRef.current) {
                clearInterval(docsPollRef.current);
                docsPollRef.current = null;
                setDocsRunning(false);
                toast.success(
                    `Perizie: ${data.state.deep_ready} aste pronte · ${data.state.perizia_found} perizie trovate` +
                    (data.state.aborted_throttled ? " (interrotto: throttling)" : "")
                );
                load();
            }
        } catch (err) {
            // silent
        }
    }, [load]);

    useEffect(() => {
        if (user && user.role === "admin") {
            load();
            fetchEnrichStatus();
            fetchDocsStatus();
        }
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
            if (docsPollRef.current) clearInterval(docsPollRef.current);
        };
    }, [user, load, fetchEnrichStatus, fetchDocsStatus]);

    const runRecompute = async () => {
        setRecomputing(true);
        try {
            const { data } = await api.post("/admin/enrich/recompute");
            toast.success(`€/m² ricalcolato · ${data.prices_nulled} prezzi errati rimossi · ${data.market_recomputed} aggiornati`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setRecomputing(false);
        }
    };

    const runRefetch = async () => {
        setRefetching(true);
        try {
            const { data } = await api.post("/admin/enrich/refetch", null, { params: { limit: 200 } });
            if (data.status === "already_running") {
                toast.info("Re-fetch già in corso");
            } else {
                toast.success("Re-fetch avviato in background");
            }
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = setInterval(fetchEnrichStatus, 4000);
            fetchEnrichStatus();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
            setRefetching(false);
        }
    };

    const runDocs = async () => {
        setDocsRunning(true);
        try {
            const { data } = await api.post("/admin/enrich/documents", null, { params: { limit: 200 } });
            if (data.status === "already_running") {
                toast.info("Ricerca perizie già in corso");
            } else {
                toast.success("Ricerca perizie avviata in background");
            }
            if (docsPollRef.current) clearInterval(docsPollRef.current);
            docsPollRef.current = setInterval(fetchDocsStatus, 4000);
            fetchDocsStatus();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
            setDocsRunning(false);
        }
    };

    const downloadPdf = async () => {
        setDownloading(true);
        try {
            const res = await api.get("/admin/coverage/export.pdf", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `audit-copertura-${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Download PDF non riuscito");
        } finally {
            setDownloading(false);
        }
    };

    // Top regioni per numero aste (aggregato dalle province)
    const topRegions = useMemo(() => {
        if (!report) return [];
        const map = {};
        for (const p of report.provinces) {
            if (p.region === "—") continue;
            map[p.region] = (map[p.region] || 0) + p.total;
        }
        return Object.entries(map).map(([region, total]) => ({ region, total })).sort((a, b) => b.total - a.total);
    }, [report]);

    const topProvinces = useMemo(() => {
        if (!report) return [];
        return [...report.provinces].sort((a, b) => b.total - a.total);
    }, [report]);

    if (user === null) return null;
    if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

    if (loading) {
        return (
            <Layout>
                <div data-testid="coverage-loading" className="py-20 text-center text-sm text-gray-500">Caricamento audit copertura…</div>
            </Layout>
        );
    }
    if (!report) {
        return (
            <Layout>
                <div className="py-20 text-center text-sm text-gray-500">Report non disponibile.</div>
            </Layout>
        );
    }

    const s = report.summary;
    const q = report.quality_national;
    const r = report.readiness;
    const rm = READINESS_META[r.level] || READINESS_META.non_pronto;
    const maxRegion = topRegions[0]?.total || 1;
    const maxProvince = topProvinces[0]?.total || 1;

    const QUALITY_FIELDS = [
        { key: "address_pct", label: "Indirizzo presente" },
        { key: "price_pct", label: "Prezzo base presente" },
        { key: "geo_pct", label: "Geolocalizzazione" },
        { key: "surface_pct", label: "Superficie (m²)" },
        { key: "price_sqm_pct", label: "€/m² calcolabile" },
        { key: "docs_pct", label: "Perizia / PDF allegata" },
        { key: "deep_analysis_pct", label: "Deep Analysis completata" },
    ];

    return (
        <Layout>
            <div data-testid="coverage-audit-page" className="overflow-x-hidden">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                    <div>
                        <div className="overline mb-2">Admin · Copertura nazionale</div>
                        <h1 className="font-display-black text-4xl mb-2 leading-tight">Audit Copertura &amp; Qualità Dati</h1>
                        <p className="text-sm text-gray-600">
                            Fotografia oggettiva dell'inventario reale. Generato il {report.generated_at?.slice(0, 16).replace("T", " ")} UTC.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={load}
                            data-testid="coverage-refresh-btn"
                            className="border border-gray-300 hover:border-black h-10 px-3 text-sm flex items-center gap-2 transition-colors"
                        >
                            <ArrowClockwise size={14} weight="bold" /> Aggiorna
                        </button>
                        <button
                            onClick={downloadPdf}
                            disabled={downloading}
                            data-testid="coverage-download-pdf-btn"
                            className="bg-black text-white hover:bg-gray-800 h-10 px-4 text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
                        >
                            <FilePdf size={16} weight="bold" /> {downloading ? "Generazione…" : "Scarica PDF"}
                        </button>
                    </div>
                </div>

                {/* Readiness */}
                <div className="border border-gray-200 bg-white p-6 mb-6 flex flex-col md:flex-row md:items-center gap-6" data-testid="coverage-readiness">
                    <div className="flex items-baseline gap-2">
                        <span className="font-display-black text-6xl tabular-nums">{r.score}</span>
                        <span className="text-gray-400 text-xl font-bold">/100</span>
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="overline">Readiness score</span>
                            <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${rm.cls}`} data-testid="coverage-readiness-level">{rm.label}</span>
                        </div>
                        <p className="text-sm text-gray-700">{r.verdict}</p>
                    </div>
                </div>

                {/* Summary stat cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                    {[
                        { v: s.regions_covered, l: "Regioni coperte", t: "stat-regions" },
                        { v: s.provinces_covered, l: "Province coperte", t: "stat-provinces" },
                        { v: s.auctions_distinct, l: "Aste reali (distinte)", t: "stat-distinct" },
                        { v: s.auctions_raw, l: "Record grezzi", t: "stat-raw" },
                        { v: s.cross_org_duplicates, l: "Duplicati neutralizzati", t: "stat-dup" },
                        { v: s.provinces_supported, l: "Province supportate", t: "stat-supported" },
                    ].map((c) => (
                        <div key={c.t} data-testid={`coverage-${c.t}`} className="border border-gray-200 bg-white p-4">
                            <div className="font-display-black text-3xl tabular-nums">{c.v}</div>
                            <div className="overline mt-1">{c.l}</div>
                        </div>
                    ))}
                </div>

                {/* Classification breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
                    {[
                        { k: "supportata", v: s.provinces_supported, icon: CheckCircle },
                        { k: "limitata", v: s.provinces_limited, icon: MapPin },
                        { k: "non_supportata", v: s.provinces_unsupported, icon: Warning },
                    ].map(({ k, v, icon: Icon }) => {
                        const m = CLASS_META[k];
                        return (
                            <div key={k} className={`border p-4 flex items-center gap-3 ${m.cls}`} data-testid={`coverage-class-${k}`}>
                                <Icon size={28} weight="bold" />
                                <div>
                                    <div className="font-display-black text-2xl tabular-nums">{v}</div>
                                    <div className="text-xs font-bold uppercase tracking-wider">{m.label}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    {/* Top regioni */}
                    <div className="border border-gray-200 bg-white p-5" data-testid="coverage-top-regions">
                        <div className="overline mb-4 flex items-center gap-2"><MapPin size={14} weight="bold" /> Top regioni per numero aste</div>
                        <div className="space-y-2">
                            {topRegions.map((row) => (
                                <DistroBar key={row.region} label={row.region} value={row.total} max={maxRegion} testid={`region-bar-${row.region}`} />
                            ))}
                        </div>
                    </div>
                    {/* Top province */}
                    <div className="border border-gray-200 bg-white p-5" data-testid="coverage-top-provinces">
                        <div className="overline mb-4 flex items-center gap-2"><MapPin size={14} weight="bold" /> Top province per numero aste</div>
                        <div className="space-y-2">
                            {topProvinces.map((p) => (
                                <DistroBar key={`${p.region}-${p.province}`} label={p.province} value={p.total} max={maxProvince} testid={`province-bar-${p.province}`} />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Quality national */}
                <div className="border border-gray-200 bg-white p-5 mb-8" data-testid="coverage-quality-national">
                    <div className="overline mb-4 flex items-center gap-2"><Database size={14} weight="bold" /> Qualità dati nazionale (% sulle {s.auctions_distinct} aste reali)</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                        {QUALITY_FIELDS.map((f) => (
                            <QualityBar key={f.key} label={f.label} pct={q[f.key] ?? 0} testid={`quality-${f.key}`} />
                        ))}
                    </div>
                </div>

                {/* Arricchimento dati */}
                <div className="border border-gray-900 bg-gray-900 text-white p-5 mb-8" data-testid="coverage-enrich-panel">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <div className="overline mb-1 text-gray-400">Arricchimento dati</div>
                            <p className="text-sm text-gray-300 max-w-2xl">
                                Migliora la qualità: <strong className="text-white">Ricalcola €/m²</strong> è immediato e a costo zero. <strong className="text-white">Re-fetch</strong> riscarica le pagine fonte (superficie, prezzo, documenti). <strong className="text-white">Trova perizie</strong> scarica le perizie PDF pubbliche (privilegiando Astalegale/PVP), estrae il testo e segna le aste "pronte per Deep Analysis". Nessuna Deep Analysis viene eseguita automaticamente.
                            </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                onClick={runRecompute}
                                disabled={recomputing}
                                data-testid="enrich-recompute-btn"
                                className="bg-white text-gray-900 hover:bg-gray-200 h-10 px-4 text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
                            >
                                <Lightning size={16} weight="bold" /> {recomputing ? "Ricalcolo…" : "Ricalcola €/m²"}
                            </button>
                            <button
                                onClick={runRefetch}
                                disabled={refetching || enrichState?.running}
                                data-testid="enrich-refetch-btn"
                                className="border border-gray-600 text-white hover:border-white h-10 px-4 text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
                            >
                                <CloudArrowDown size={16} weight="bold" /> {(refetching || enrichState?.running) ? "Re-fetch in corso…" : "Re-fetch dati reali"}
                            </button>
                            <button
                                onClick={runDocs}
                                disabled={docsRunning || docsState?.running}
                                data-testid="enrich-docs-btn"
                                className="border border-gray-600 text-white hover:border-white h-10 px-4 text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
                            >
                                <FileMagnifyingGlass size={16} weight="bold" /> {(docsRunning || docsState?.running) ? "Ricerca perizie…" : "Trova perizie"}
                            </button>
                        </div>
                    </div>
                    {enrichState && (enrichState.running || enrichState.processed > 0) && (
                        <div className="mt-4 pt-4 border-t border-gray-700" data-testid="enrich-status">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-gray-400">
                                    {enrichState.running ? "Re-fetch in corso" : (enrichState.aborted_throttled ? "Re-fetch interrotto (throttling)" : "Re-fetch completato")}
                                    {" · "}{enrichState.processed}/{enrichState.total} processate
                                </span>
                                <span className="text-xs tabular-nums text-gray-300">
                                    arricchite {enrichState.enriched} · superficie {enrichState.got_surface} · documenti {enrichState.got_docs} · fallite {enrichState.failed}
                                </span>
                            </div>
                            <div className="h-2 bg-gray-700 overflow-hidden">
                                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${enrichState.total ? Math.round((enrichState.processed / enrichState.total) * 100) : 0}%` }} />
                            </div>
                        </div>
                    )}
                    {docsState && (docsState.running || docsState.processed > 0) && (
                        <div className="mt-4 pt-4 border-t border-gray-700" data-testid="docs-enrich-status">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-gray-400">
                                    {docsState.running ? "Ricerca perizie in corso" : (docsState.aborted_throttled ? "Ricerca interrotta (throttling)" : "Ricerca completata")}
                                    {" · "}{docsState.processed}/{docsState.total} processate
                                </span>
                                <span className="text-xs tabular-nums text-gray-300">
                                    perizie {docsState.perizia_found} · testo estratto {docsState.text_extracted} · pronte deep {docsState.deep_ready} · fallite {docsState.failed}
                                </span>
                            </div>
                            <div className="h-2 bg-gray-700 overflow-hidden">
                                <div className="h-full bg-indigo-400 transition-all" style={{ width: `${docsState.total ? Math.round((docsState.processed / docsState.total) * 100) : 0}%` }} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Documenti & Perizie */}
                <div className="border border-gray-200 bg-white p-5 mb-8" data-testid="coverage-documents">
                    <div className="overline mb-4 flex items-center gap-2"><FileMagnifyingGlass size={14} weight="bold" /> Documenti &amp; Perizie · Deep Analysis (manuale)</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4 mb-4">
                        <QualityBar label="Perizie trovate" pct={q.perizia_found_pct ?? 0} testid="quality-perizia_found_pct" />
                        <QualityBar label="Testo perizia estratto" pct={q.perizia_text_pct ?? 0} testid="quality-perizia_text_pct" />
                        <QualityBar label="Aste pronte per Deep Analysis" pct={q.deep_ready_pct ?? 0} testid="quality-deep_ready_pct" />
                    </div>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-gray-500">
                        <span>Perizie trovate: <strong className="text-gray-800 tabular-nums">{report.documents?.perizia_found_total ?? 0}</strong></span>
                        <span>Testo estratto: <strong className="text-gray-800 tabular-nums">{report.documents?.perizia_text_total ?? 0}</strong></span>
                        <span>Pronte per Deep Analysis: <strong className="text-gray-800 tabular-nums">{q.deep_ready_total ?? 0}</strong></span>
                        <span>Deep Analysis completate: <strong className="text-gray-800 tabular-nums">{q.deep_analysis_total ?? 0}</strong></span>
                    </div>
                    {report.documents?.by_source?.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-100" data-testid="coverage-doc-sources">
                            <div className="overline mb-2">Fonti documenti più efficaci</div>
                            <div className="flex flex-wrap gap-2">
                                {report.documents.by_source.map((src) => (
                                    <span key={src.source} className="inline-flex items-center gap-2 text-xs border border-gray-200 px-2 py-1" data-testid={`doc-source-${src.source}`}>
                                        <span className="font-semibold text-gray-800">{src.source}</span>
                                        <span className="tabular-nums text-gray-500">{src.count}</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    <p className="text-xs text-gray-400 mt-3">
                        La Deep Analysis NON viene eseguita automaticamente: le aste "pronte" hanno una perizia reale con testo estratto e possono essere promosse manualmente dal Radar.
                    </p>
                </div>

                {/* Monitoraggio scheduler notturno */}
                <PerizieMonitor />

                {/* Confronto KPI pre/post scheduler */}
                <KpiComparison />

                {/* Audit per-fonte (contributo copertura) */}
                <SourceAudit />

                {/* OCR perizie (Tesseract locale) */}
                <OcrPanel />

                {/* Criticità */}
                {report.criticita?.length > 0 && (
                    <div className="border border-amber-300 bg-amber-50 p-5 mb-8" data-testid="coverage-criticita">
                        <div className="overline mb-3 flex items-center gap-2 text-amber-800"><Warning size={14} weight="bold" /> Criticità rilevate</div>
                        <ul className="space-y-2">
                            {report.criticita.map((c, i) => (
                                <li key={i} className="text-sm text-amber-900 flex gap-2" data-testid={`criticita-${i}`}>
                                    <span className="text-amber-500 font-bold">•</span> {c}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Distribuzione aste per provincia (tabella completa) */}
                <div className="border border-gray-200 bg-white" data-testid="coverage-province-table">
                    <div className="px-5 py-4 border-b border-gray-200">
                        <div className="overline">Distribuzione aste per provincia · qualità &amp; classificazione</div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                                    <th className="px-4 py-3 font-bold">Regione</th>
                                    <th className="px-4 py-3 font-bold">Provincia</th>
                                    <th className="px-3 py-3 font-bold text-right">Tot.</th>
                                    <th className="px-3 py-3 font-bold text-right">Attive</th>
                                    <th className="px-3 py-3 font-bold text-right">Fresche 7g</th>
                                    <th className="px-3 py-3 font-bold text-right">Prezzo</th>
                                    <th className="px-3 py-3 font-bold text-right">Geo</th>
                                    <th className="px-3 py-3 font-bold text-right">Superf.</th>
                                    <th className="px-3 py-3 font-bold text-right">Perizie</th>
                                    <th className="px-3 py-3 font-bold text-right">Deep</th>
                                    <th className="px-4 py-3 font-bold">Stato</th>
                                </tr>
                            </thead>
                            <tbody>
                                {report.provinces.map((p) => (
                                    <tr key={`${p.region}-${p.province}`} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`province-row-${p.province}`}>
                                        <td className="px-4 py-3 text-gray-600">{p.region}</td>
                                        <td className="px-4 py-3 font-semibold">{p.province}</td>
                                        <td className="px-3 py-3 text-right tabular-nums">{p.total}</td>
                                        <td className="px-3 py-3 text-right tabular-nums">{p.active}</td>
                                        <td className="px-3 py-3 text-right tabular-nums text-gray-500">{p.active_fresh_7d}</td>
                                        <td className="px-3 py-3 text-right tabular-nums text-gray-500">{p.quality.price_pct}%</td>
                                        <td className="px-3 py-3 text-right tabular-nums text-gray-500">{p.quality.geo_pct}%</td>
                                        <td className="px-3 py-3 text-right tabular-nums text-gray-500">{p.quality.surface_pct}%</td>
                                        <td className="px-3 py-3 text-right tabular-nums text-gray-500">{p.quality.docs_pct}%</td>
                                        <td className="px-3 py-3 text-right tabular-nums text-gray-500">{p.quality.deep_analysis}</td>
                                        <td className="px-4 py-3"><ClassBadge value={p.classification} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Note soglie */}
                <p className="text-xs text-gray-400 mt-4" data-testid="coverage-thresholds-note">
                    Soglie attuali (solo classificazione del report, non vincolano la UX pubblica): "supportata" ≥{report.thresholds.supported_min_active} aste attive, fresche ≤{report.thresholds.supported_fresh_days}g, prezzo ≥{report.thresholds.supported_min_price_pct}%, geo ≥{report.thresholds.supported_min_geo_pct}%; "limitata" ≥{report.thresholds.limited_min_active} aste attive. Fonti: {s.sources.join(", ")}. Aggiornamento: {s.update_frequency}.
                </p>
            </div>
        </Layout>
    );
}
